import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';
import { useGetProgressQuery, useUpdateProgressMutation, useGetModulesQuery } from '../redux/Clinictrainingapi';

const ProgressContext = createContext(null);

export function ProgressProvider({ children }) {
  const { user, isAuthenticated } = useAuth();
  const [progress, setProgress] = useState({});
  const [progressReady, setProgressReady] = useState(false);

  // Fetch server progress when authenticated
  const { data: serverProgress, isLoading: progressLoading } = useGetProgressQuery(undefined, {
    skip: !isAuthenticated,
  });

  const [updateProgressApi] = useUpdateProgressMutation();

  // Fetch modules so we can map DB IDs ↔ order values
  const { data: modules, isLoading: modulesLoading } = useGetModulesQuery(undefined, {
    skip: !isAuthenticated,
  });

  // Clear progress on logout
  useEffect(() => {
    if (!isAuthenticated) {
      setProgress({});
      setProgressReady(false);
    }
  }, [isAuthenticated]);

  // Hydrate local state from server progress
  // Maps database IDs from the server → order values used by the UI
  // Waits for BOTH serverProgress AND modules to be available
  useEffect(() => {
    if (!isAuthenticated) return;
    if (!Array.isArray(serverProgress)) return;
    if (!Array.isArray(modules)) return;

    // Empty progress array = new user, still "ready"
    if (serverProgress.length === 0) {
      setProgress({});
      setProgressReady(true);
      return;
    }

    const mapped = {};
    serverProgress.forEach((entry) => {
      // Find the module by its DB id to get its order
      const mod = modules.find((m) => m.id === entry.module);
      if (!mod) return;

      const moduleOrder = mod.order;
      const key = `m${moduleOrder}`;

      // Convert completed section DB IDs → section order values
      const completedOrders = (entry.completed_sections ?? [])
        .map((sectionId) => {
          const section = (mod.sections ?? []).find((s) => s.id === sectionId);
          return section?.order ?? null;
        })
        .filter((o) => o !== null);

      // Convert current_section DB ID → order value
      const currentSectionObj = (mod.sections ?? []).find(
        (s) => s.id === entry.current_section
      );

      mapped[key] = {
        completedLessons: completedOrders,
        currentSection: currentSectionObj?.order ?? null,
        isCompleted: entry.is_completed ?? false,
        lastAccessed: entry.updated_at ? new Date(entry.updated_at).getTime() : Date.now(),
      };
    });
    setProgress(mapped);
    setProgressReady(true);

    // Also cache in localStorage as fallback
    if (user?.id) {
      localStorage.setItem(`nexalife_progress_${user.id}`, JSON.stringify(mapped));
    }
  }, [serverProgress, modules, user?.id, isAuthenticated]);

  const save = (next) => {
    if (user?.id) {
      localStorage.setItem(`nexalife_progress_${user.id}`, JSON.stringify(next));
    }
  };

  // completeLesson(moduleOrder, lessonOrder, { moduleDbId, sectionDbId })
  // moduleOrder & lessonOrder  → local state keys (display order)
  // moduleDbId  & sectionDbId  → real DB IDs sent to the backend
  const completeLesson = useCallback((moduleOrder, lessonOrder, { moduleDbId, sectionDbId } = {}) => {
    // Update local state immediately (optimistic)
    setProgress(prev => {
      const key = `m${moduleOrder}`;
      const lessons = prev[key]?.completedLessons || [];
      if (lessons.includes(lessonOrder)) return prev;
      const next = {
        ...prev,
        [key]: {
          ...prev[key],
          completedLessons: [...lessons, lessonOrder],
          currentSection: lessonOrder,
          lastAccessed: Date.now(),
        }
      };
      save(next);
      return next;
    });

    // Sync to backend using real database IDs
    if (moduleDbId && sectionDbId) {
      updateProgressApi({
        module_id: moduleDbId,
        section_id: sectionDbId,
        mark_completed: true,
      });
    }
  }, [updateProgressApi]);

  const completeQuiz = useCallback((moduleId, score, total) => {
    setProgress(prev => {
      const key = `m${moduleId}`;
      const next = {
        ...prev,
        [key]: {
          ...prev[key],
          quizCompleted: true,
          quizScore: score,
          quizTotal: total,
          completedAt: Date.now(),
        }
      };
      save(next);
      return next;
    });
  }, []);

  const getModuleProgress = (moduleId) => {
    return progress[`m${moduleId}`] || { completedLessons: [] };
  };

  const isLessonComplete = (moduleId, lessonId) => {
    return getModuleProgress(moduleId).completedLessons?.includes(lessonId) ?? false;
  };

  const isModuleComplete = (moduleId, totalLessons) => {
    const mp = getModuleProgress(moduleId);
    return mp.isCompleted || (mp.quizCompleted && (mp.completedLessons?.length || 0) >= totalLessons);
  };

  const getOverallProgress = (modules) => {
    let completed = 0;
    modules.forEach(m => {
      if (isModuleComplete(m.order, m.sections?.length ?? 0)) completed++;
    });
    return { completed, total: modules.length };
  };

  return (
    <ProgressContext.Provider value={{
      progress, completeLesson, completeQuiz,
      getModuleProgress, isLessonComplete, isModuleComplete, getOverallProgress,
      serverProgress, progressReady,
    }}>
      {children}
    </ProgressContext.Provider>
  );
}

export const useProgress = () => {
  const ctx = useContext(ProgressContext);
  if (!ctx) throw new Error('useProgress must be used within ProgressProvider');
  return ctx;
};
